/**
 * Empire 12.3 PDP orchestrator (Zeckos custom replacement)
 *
 * Original file (pre-2026-05-15) was dead jQuery code referencing CSS classes
 * that do not exist on Empire 12.3 — every productive line was commented out.
 * The PDP relied on this file to wire <variant-selection>'s variant-change
 * event to the price block, the gallery, and the add-to-cart button. Without
 * it, customers clicking color swatches saw the swatch state update but the
 * price, image, and ATC button did NOT react — confirmed via Microsoft Clarity
 * recordings showing rage-clicking and abandonment.
 *
 * What this script does on each variant-change event:
 *   1. <product-gallery>:
 *        — hides slides + thumbnails whose alt text begins with a different
 *          variant-option value (e.g. "Black - Image 1 -" gets hidden when
 *          Silver is selected). Images with no variant prefix (e.g. "COLOR")
 *          are treated as shared and stay visible.
 *        — calls swiper.update() and slides to the variant's featured_media.
 *   2. Price block:
 *        — replaces every [data-price] textContent with the variant's price
 *        — toggles [data-price-compare-container] visibility based on
 *          compare_at_price > price
 *        — hides range elements ([data-price-min], [data-price-max])
 *   3. Add-to-cart button:
 *        — enables / disables based on variant.available
 *        — swaps the label between "Add to cart" / "Sold out" / "Unavailable"
 *          using the strings the section script-tag JSON exposes
 *   4. URL: history.replaceState with ?variant={id}
 *
 * Variant-image affinity is determined by alt-text prefix (the Zeckos image
 * naming convention is "{Color} - Image N - {description}"). If an alt text's
 * first whitespace-or-hyphen-delimited token matches one of the product's
 * option1 values, it belongs to that variant. Otherwise it's shared.
 *
 * Money formatting uses Intl.NumberFormat (USD only — Zeckos is US-only).
 *
 * On initial page load, this script reads the URL's ?variant= param (or the
 * first available variant if absent) and runs the same update chain so the
 * page state matches the URL even before the user interacts.
 *
 * Maintenance: this file is a Zeckos custom replacement of Pixel Union's
 * default section-static-product.js. If Empire is upgraded, compare upstream's
 * new version before overwriting — the upstream may have shipped a proper
 * orchestrator that supersedes this. Original (broken) file is preserved at
 * `shopify-theme-edits/section-static-product.js.original-2026-05-15.js` in
 * the ZeckosHub repo.
 */

(() => {
  const moneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  const money = (cents) => moneyFormatter.format((cents || 0) / 100);

  function init() {
    const variantSelection = document.querySelector('variant-selection');
    if (!variantSelection) return;

    const productGallery = document.querySelector('product-gallery');
    const productForm   = document.querySelector('form[action*="/cart/add"]');
    const addToCartBtn  = productForm
      ? productForm.querySelector('button[name="add"], [type="submit"]')
      : null;

    // Section script-tag JSON — the section template injects product data,
    // i18n strings, and settings into a <script type="application/json">.
    // Multiple sections on the page each emit their own data-section-data
    // tag — we MUST filter by data-section-type="static-product" or we'd
    // grab the header/footer's settings JSON (which has no product field).
    const sectionDataEl = document.querySelector('script[data-section-data][data-section-type="static-product"]');
    let sectionData = {};
    try { sectionData = JSON.parse(sectionDataEl?.textContent || '{}'); } catch (_) {}
    const ctx = sectionData.context || {};
    const product = sectionData.product || null;

    // Gallery handling — Empire-native (2026-05-15 rev 2).
    //
    // The previous approach hid non-matching slides via display:none so only
    // the selected variant's images showed. That fought Empire's Swiper: its
    // #setupThumbnailsGrid binds every thumbnail click to
    // swiper.slideTo(ABSOLUTE_index), and #handleSlideChange tracks the active
    // thumb by swiper.activeIndex. With display:none slides still in Swiper's
    // index space, clicking a visible thumbnail landed on a hidden slide
    // (blank preview) and the active-thumb highlight desynced from the main
    // image. Patching around Swiper's absolute-index model proved fragile.
    //
    // We now use Empire's built-in behavior: keep ALL slides in the carousel
    // (indices stay dense — thumbnail clicks + active-thumb sync work via
    // Empire's own slideChange handler) and just slide the main image to the
    // selected variant's featured media on variant-change. Trade-off accepted
    // by the operator: the strip shows every variant's images, but the main
    // image is always correct and there are zero index bugs. The robust
    // "show only this variant's images" UX is deferred to the ZH-direct
    // Shopify catalog work (variant-image association via the Admin API), see
    // ~/.claude/plans/2026-05-15-per-variant-content-metafield-swap.md and the
    // Phase G discussion in the handoff.
    function updateGalleryToVariant(variant) {
      if (!productGallery || !variant) return;
      // showVariantMedia is Empire's public method:
      //   if (!variant.featured_media) return;
      //   this.#showMediaAtIndex(variant.featured_media.position - 1)  // swiper.slideTo
      // Empire's slideChange hook then re-highlights the correct thumbnail.
      if (typeof productGallery.showVariantMedia === 'function') {
        try { productGallery.showVariantMedia(variant); } catch (_) {}
        return;
      }
      // Fallback if Empire's API name ever changes: slide by featured position.
      if (variant.featured_media && variant.featured_media.position
          && productGallery.swiper
          && typeof productGallery.swiper.slideTo === 'function') {
        try { productGallery.swiper.slideTo(variant.featured_media.position - 1); } catch (_) {}
      }
    }

    function updatePrice(variant) {
      if (!variant) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      // Current price (single value)
      document.querySelectorAll('[data-price]').forEach((el) => {
        el.textContent = money(variant.price);
        el.style.display = '';
      });

      // Hide range elements (no longer relevant once a variant is chosen)
      document.querySelectorAll('[data-price-min], [data-price-max]').forEach((el) => {
        el.style.display = 'none';
      });

      // Strip "varies" class so wrapper styling reflects single-price state
      document.querySelectorAll('.price--varies').forEach((el) => {
        el.classList.remove('price--varies');
      });

      // Compare-at price
      const compareContainers = document.querySelectorAll('[data-price-compare-container]');
      compareContainers.forEach((c) => {
        c.classList.toggle('visible', !!onSale);
      });
      document.querySelectorAll('[data-price-compare]').forEach((el) => {
        if (onSale) {
          el.textContent = money(variant.compare_at_price);
          el.style.display = '';
        } else {
          el.textContent = '';
        }
      });
      document.querySelectorAll('[data-price-compare-min], [data-price-compare-max]').forEach((el) => {
        el.style.display = 'none';
      });

      // Sale-state class on the current-price block (Empire styles this red)
      document.querySelectorAll('.price__current').forEach((el) => {
        el.classList.toggle('price__current--on-sale', !!onSale);
      });
    }

    function setButtonLabel(btn, label) {
      if (!btn || !label) return;
      // Empire's button has either a .button__text span or just text content.
      const span = btn.querySelector('.button__text, .product-form__submit-text, span');
      if (span) span.textContent = label;
      else btn.textContent = label;
    }

    function updateAddToCart(variant) {
      if (!addToCartBtn) return;
      if (variant && variant.available) {
        addToCartBtn.disabled = false;
        addToCartBtn.removeAttribute('aria-disabled');
        addToCartBtn.classList.remove('disabled');
        if (ctx.product_available) setButtonLabel(addToCartBtn, ctx.product_available);
      } else if (variant && !variant.available) {
        addToCartBtn.disabled = true;
        addToCartBtn.setAttribute('aria-disabled', 'true');
        addToCartBtn.classList.add('disabled');
        if (ctx.product_sold_out) setButtonLabel(addToCartBtn, ctx.product_sold_out);
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.setAttribute('aria-disabled', 'true');
        addToCartBtn.classList.add('disabled');
        if (ctx.product_unavailable) setButtonLabel(addToCartBtn, ctx.product_unavailable);
      }
    }

    function updateUrl(variant) {
      if (!variant) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url.toString());
      } catch (_) { /* non-fatal */ }
    }

    function updateSku(variant) {
      // Empire renders the child SKU server-side from the URL variant, so it
      // only "changed on refresh" before. variant.sku is on every variant
      // object — swap the [data-product-sku] span on every variant-change.
      // NOTE: this updates the SKU only. Per-variant Title / bullets /
      // description is NOT possible client-side without per-variant content
      // metafields (Shopify products carry ONE title + body_html; variants
      // differ only by option values, price, image, sku, weight, inventory).
      // See the handoff for the metafield-backed content-swap option.
      if (!variant || !variant.sku) return;
      document.querySelectorAll('[data-product-sku]').forEach((el) => {
        el.textContent = variant.sku;
      });
    }

    function applyVariant(variant) {
      try { updateGalleryToVariant(variant); } catch (e) { console.warn('section-static-product: updateGalleryToVariant failed', e); }
      try { updatePrice(variant); }            catch (e) { console.warn('section-static-product: updatePrice failed', e); }
      try { updateAddToCart(variant); }        catch (e) { console.warn('section-static-product: updateAddToCart failed', e); }
      try { updateSku(variant); }              catch (e) { console.warn('section-static-product: updateSku failed', e); }
      try { updateUrl(variant); }              catch (e) { console.warn('section-static-product: updateUrl failed', e); }
    }

    // Live wiring
    variantSelection.addEventListener('variant-change', (event) => {
      const detail = event.detail || {};
      const v = detail.variant;
      if (v) {
        applyVariant(v);
      } else {
        // Selected option combo doesn't map to a real variant
        updateAddToCart(null);
      }
    });

    // Initial sync — match the URL's ?variant= param (or first available)
    function initialVariant() {
      if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return null;
      const urlVariantId = new URLSearchParams(window.location.search).get('variant');
      if (urlVariantId) {
        const match = product.variants.find((v) => String(v.id) === urlVariantId);
        if (match) return match;
      }
      return product.variants.find((v) => v.available) || product.variants[0];
    }

    const initial = initialVariant();
    if (initial) {
      // Defer one tick so swiper finishes its connectedCallback init first
      requestAnimationFrame(() => applyVariant(initial));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
